/**
 * DomainProgressBanner.tsx
 *
 * Per-domain fetch progress narration banner — rendered while `isSyncing` is true.
 * Matches mockup 15 (`2026-04-23-15-canvas-loading-v3.html`).
 *
 * Visual spec:
 *   - Role="status" aria-live="polite" — screen readers announce progress without interruption.
 *   - Spinner (CSS animation): accent border-top against muted accent-bg ring.
 *   - Domain tags row:
 *       done    → bg-green-100 text-green-700, checkmark icon, aria-label "…: complete"
 *       active  → bg-accent/10 text-accent, mini spinner icon, aria-label "…: loading"
 *       pending → bg-muted text-muted-foreground border border-border, circle icon, aria-label "…: waiting"
 *   - Sub-label: shows what the active domain is currently fetching.
 *
 * This component is purely presentational — it accepts pre-computed props
 * and does not connect to Redux directly. PanelCanvasV1 selects from the
 * store and passes the relevant slices down.
 */

import React from 'react'
import { Badge } from '@/components/ui/badge'

// ─── Types ────────────────────────────────────────────────────────────────────

export type DomainTagStatus = 'done' | 'active' | 'pending'

export interface DomainProgressEntry {
  domainId: string
  displayName: string
  /** Computed from DomainSyncStatus: success → done, fetching → active, else → pending */
  tagStatus: DomainTagStatus
}

interface DomainProgressBannerProps {
  /** Ordered list of domains with their current tag status. */
  domains: DomainProgressEntry[]
  /**
   * Sub-label shown below the domain tags.
   * e.g. "Fetching financial records — this usually takes 10–20 seconds."
   * Pass null when no active domain is fetching.
   */
  sublabel: string | null
}

// ─── DomainTag ────────────────────────────────────────────────────────────────

const DomainTag: React.FC<{ entry: DomainProgressEntry }> = ({ entry }) => {
  const { displayName, tagStatus } = entry

  if (tagStatus === 'done') {
    return (
      <span aria-label={`${displayName} data: complete`}>
        <Badge className="gap-1 bg-success-foreground text-success border-success border hover:bg-success-foreground">
          {displayName}
        </Badge>
      </span>
    )
  }

  if (tagStatus === 'active') {
    return (
      <span aria-label={`${displayName} data: loading`}>
        <Badge className="gap-1 bg-accent-secondary text-accent hover:bg-accent-secondary border-accent border">
          {displayName}
        </Badge>
      </span>
    )
  }

  // pending
  return (
    <span aria-label={`${displayName} data: waiting`}>
      <Badge className="gap-1 bg-warning-foreground text-warning border border-border hover:bg-warning-secondary">
        {displayName}
      </Badge>
    </span>
  )
}

// ─── DomainProgressBanner ────────────────────────────────────────────────────

export const DomainProgressBanner: React.FC<DomainProgressBannerProps> = ({
  domains,
  sublabel,
}) => (
  <div
    className="bg-background border-b border-border px-6 py-3.5 flex-shrink-0 w-full flex items-center justify-center"
    role="status"
    aria-live="polite"
    aria-label="Data loading progress"
    data-testid="domain-progress-banner"
  >
    <div className="flex items-start gap-3 max-w-3xl">
      {/* Spinner */}
      <div
        className="w-5 h-5 rounded-full border-[2.5px] border-accent/20 border-t-accent border-background animate-spin flex-shrink-0 mt-0.5"
        aria-hidden="true"
        data-testid="banner-spinner"
      />

      {/* Body */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-primary mb-1.5">
          Fetching the newest data for following domains:
        </p>

        {/* Domain tags */}
        {domains.length > 0 && (
          <div
            className="flex flex-wrap gap-2"
            data-testid="domain-tags-row"
          >
            {domains.map(entry => (
              <DomainTag key={entry.domainId} entry={entry} />
            ))}
          </div>
        )}

        {/* Sub-label */}
        {sublabel && (
          <p
            className="text-xs text-secondary mt-1.5"
            data-testid="banner-sublabel"
          >
            {sublabel}
          </p>
        )}
      </div>
    </div>
  </div>
)
